import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    fullName: '',
    organization: '',
    reason: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.username, formData.password);
        const from = location.state?.from;
        navigate(from ? `${from.pathname}${from.search || ''}` : '/');
      } else {
        const result = await register(
          formData.username,
          formData.password,
          formData.email,
          formData.fullName,
          formData.organization,
          formData.reason
        );

        if (result.status === 'active' || result.status === 'approved') {
          // Whitelisted emails are approved automatically at registration
          setSuccess('Account created! You can now log in.');
          setIsLogin(true);
          setFormData({ ...formData, password: '' });
        } else {
          setSuccess('Registration submitted! An administrator will review your request.');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div className="glass rise-in" style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>ARMITA</h1>
          <p style={styles.tagline}>Medical Imaging Platform</p>
        </div>

        <div className="seg" style={styles.tabs}>
          <button
            type="button"
            className={`seg__opt${isLogin ? ' seg__opt--active' : ''}`}
            style={styles.tab}
            onClick={() => { setIsLogin(true); setError(''); setSuccess(''); }}
          >
            Sign In
          </button>
          <button
            type="button"
            className={`seg__opt${!isLogin ? ' seg__opt--active' : ''}`}
            style={styles.tab}
            onClick={() => { setIsLogin(false); setError(''); setSuccess(''); }}
          >
            Request Access
          </button>
        </div>

        {error && <div className="alert alert--danger" role="alert" style={styles.banner}>{error}</div>}
        {success && <div className="alert alert--success" role="alert" style={styles.banner}>{success}</div>}

        <div style={styles.formContainer}>
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label className="field__label" htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="input"
                placeholder="Enter username"
                autoComplete="username"
                required
                minLength={isLogin ? undefined : 3}
              />
            </div>

            {!isLogin && (
              <>
                <div className="field">
                  <label className="field__label" htmlFor="email">Email <span style={styles.required}>*</span></label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input"
                    placeholder="email@organization.com"
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="fullName">Full Name</label>
                  <input
                    id="fullName"
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="input"
                    placeholder="Dr. John Smith"
                    autoComplete="name"
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="organization">Organization</label>
                  <input
                    id="organization"
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="input"
                    placeholder="Hospital / Institution"
                    autoComplete="organization"
                  />
                </div>
              </>
            )}

            <div className="field">
              <label className="field__label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="input"
                placeholder={isLogin ? "Enter password" : "Min 8 characters"}
                autoComplete={isLogin ? "current-password" : "new-password"}
                required
                minLength={isLogin ? undefined : 8}
              />
            </div>

            {!isLogin && (
              <div className="field">
                <label className="field__label" htmlFor="reason">Reason for Access <span style={styles.optional}>(optional)</span></label>
                <textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  className="textarea"
                  style={styles.textarea}
                  placeholder="Brief description of intended use..."
                  rows={2}
                />
              </div>
            )}

            <button
              type="submit"
              className="btn btn--primary btn--block btn--lg"
              style={styles.submit}
              disabled={loading}
            >
              {loading && <span className="spinner spinner--sm" aria-hidden="true" />}
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Submit Request')}
            </button>

            {isLogin && (
              <div style={styles.forgotRow}>
                <Link to="/forgot-password" style={styles.forgotLink}>
                  Forgot password?
                </Link>
              </div>
            )}
          </form>

          {!isLogin && (
            <div className="alert alert--info" style={styles.infoBox}>
              <span style={styles.infoIcon} aria-hidden="true">i</span>
              <span>
                Whitelisted organization emails are auto-approved. Others require admin review.
              </span>
            </div>
          )}
        </div>

        <p style={styles.footer}>
          Accurate Researched Medical Imaging Technology w/ AI
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-canvas)',
    padding: 'var(--space-4)',
    boxSizing: 'border-box'
  },
  card: {
    padding: 'clamp(24px, 4vw, 36px)',
    width: '100%',
    maxWidth: '440px',
    maxHeight: 'min(92dvh, 92vh)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--space-5)',
    flexShrink: 0
  },
  logo: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
    letterSpacing: '0.22em'
  },
  tagline: {
    fontSize: '0.88rem',
    color: 'var(--text-secondary)',
    margin: '6px 0 0 0',
    letterSpacing: '-0.01em'
  },
  tabs: {
    display: 'flex',
    width: '100%',
    marginBottom: 'var(--space-4)',
    flexShrink: 0,
    boxSizing: 'border-box'
  },
  tab: {
    flex: 1
  },
  formContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0
  },
  banner: {
    marginBottom: 'var(--space-3)',
    flexShrink: 0
  },
  required: {
    color: 'var(--danger)'
  },
  optional: {
    color: 'var(--text-tertiary)',
    fontWeight: 400,
    textTransform: 'none'
  },
  textarea: {
    minHeight: '64px'
  },
  submit: {
    marginTop: 'var(--space-2)'
  },
  forgotRow: {
    textAlign: 'center',
    marginTop: 'var(--space-3)'
  },
  forgotLink: {
    color: 'var(--accent)',
    fontSize: '0.85rem',
    textDecoration: 'none'
  },
  infoBox: {
    fontSize: '0.8rem',
    marginTop: 'var(--space-4)',
    color: 'var(--text-secondary)'
  },
  infoIcon: {
    backgroundColor: 'var(--accent-soft)',
    color: 'var(--accent)',
    borderRadius: 'var(--radius-full)',
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 700,
    fontStyle: 'italic',
    flexShrink: 0
  },
  footer: {
    textAlign: 'center',
    fontSize: '0.72rem',
    color: 'var(--text-tertiary)',
    margin: 'var(--space-4) 0 0 0',
    letterSpacing: '0.02em',
    flexShrink: 0
  }
};

export default LoginPage;
